import React, { useMemo } from 'react';
import {
  Row, Col, Input, Select, Typography,
} from 'antd';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import useThaiLocation from '../../hooks/useThaiLocation';

const { Text } = Typography;
const { TextArea } = Input;

interface ThaiAddressFieldsProps {
  prefix: string; // 'home' | 'work'
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watchProvince: string;
  watchDistrict: string;
  watchSubDistrict: string;
}

const ThaiAddressFields: React.FC<ThaiAddressFieldsProps> = ({
  prefix,
  control,
  setValue,
  watchProvince,
  watchDistrict,
  watchSubDistrict,
}) => {
  const {
    loaded, provinces, getDistrictsByProvince, getSubDistrictsByDistrict, getZipCode,
  } = useThaiLocation();

  const provinceOptions = useMemo(
    () => provinces.map((p) => ({ label: p.name_th, value: p.name_th })),
    [provinces],
  );

  const districtOptions = useMemo(
    () => (watchProvince
      ? getDistrictsByProvince(watchProvince).map((d) => ({ label: d.name_th, value: d.name_th }))
      : []),
    [watchProvince, loaded],
  );

  const subDistrictOptions = useMemo(
    () => (watchDistrict && watchProvince
      ? getSubDistrictsByDistrict(watchDistrict, watchProvince)
        .map((s) => ({ label: s.name_th, value: s.name_th }))
      : []),
    [watchDistrict, watchProvince, loaded],
  );

  const addressLineField = `${prefix}AddressLine` as const;
  const subDistrictField = `${prefix}SubDistrict` as const;
  const districtField = `${prefix}District` as const;
  const provinceField = `${prefix}Province` as const;
  const postalCodeField = `${prefix}PostalCode` as const;

  return (
    <Row gutter={[32, 32]} style={{ marginTop: 1 }}>
      <Col xs={24}>
        <Text>Address (house no., soi, road)</Text>
        <Controller
          name={addressLineField}
          control={control}
          render={({ field }) => (
            <TextArea rows={2} placeholder="House no., soi, road" {...field} />
          )}
        />
      </Col>
      <Col sm={12} xs={24}>
        <Text>Province</Text>
        <Controller
          name={provinceField}
          control={control}
          render={({ field }) => (
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              options={provinceOptions}
              value={field.value || undefined}
              placeholder="Select province"
              onChange={(newValue) => {
                field.onChange(newValue || '');
                setValue(districtField, '');
                setValue(subDistrictField, '');
                setValue(postalCodeField, '');
              }}
              onBlur={field.onBlur}
              notFoundContent="No province found"
              loading={!loaded}
            />
          )}
        />
      </Col>
      <Col sm={12} xs={24}>
        <Text>District (Amphoe / Khet)</Text>
        <Controller
          name={districtField}
          control={control}
          render={({ field }) => (
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              options={districtOptions}
              value={field.value || undefined}
              placeholder="Select district"
              onChange={(newValue) => {
                field.onChange(newValue || '');
                setValue(subDistrictField, '');
                setValue(postalCodeField, '');
              }}
              onBlur={field.onBlur}
              notFoundContent={watchProvince ? 'No district found' : 'Select province first'}
              disabled={!watchProvince}
              loading={!loaded}
            />
          )}
        />
      </Col>
      <Col sm={12} xs={24}>
        <Text>Sub-district (Tambon / Khwaeng)</Text>
        <Controller
          name={subDistrictField}
          control={control}
          render={({ field }) => (
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              options={subDistrictOptions}
              value={field.value || undefined}
              placeholder="Select sub-district"
              onChange={(newValue) => {
                field.onChange(newValue || '');
                if (newValue && watchDistrict && watchProvince) {
                  const zip = getZipCode(newValue, watchDistrict, watchProvince);
                  setValue(postalCodeField, zip);
                } else {
                  setValue(postalCodeField, '');
                }
              }}
              onBlur={field.onBlur}
              notFoundContent={watchDistrict ? 'No sub-district found' : 'Select district first'}
              disabled={!watchDistrict}
              loading={!loaded}
            />
          )}
        />
      </Col>
      <Col sm={12} xs={24}>
        <Text>Postal code</Text>
        <Controller
          name={postalCodeField}
          control={control}
          render={({ field }) => (
            <Input placeholder="Postal code" maxLength={5} {...field} disabled />
          )}
        />
      </Col>
    </Row>
  );
};

export default ThaiAddressFields;
